/* eslint-disable react-refresh/only-export-components */

import React, { useContext, useMemo } from "react";
import type { ReactNode } from "react";
import { useU2000GridStateHandlers } from "../u2000Core/reducer/formReducer/U2000GridStateHandlers";
import { useU2000SearchStateHandlers } from "../u2000Core/reducer/formReducer/U2000SearchStateHandlers";
import type { U2000GridStateHandlersReturn } from "../u2000Core/reducer/formReducer/U2000GridStateHandlers";
import type { U2000SearchStateHandlersReturn } from "../u2000Core/reducer/formReducer/U2000SearchStateHandlers";
import { useU2000LUStateHandlers } from "../u2000Core/reducer/formReducer/U2000LUHandlers";
import type { U2000LUState } from "../u2000Core/reducer/formReducer/U2000LUState";
import type { U2104Affiliation, U2104Organization, U2104Role } from "./U2104Service";

// Type for initLU (from LUState.initLU)
export type InitLU = U2000LUState["personalization"] & { launchSearch: boolean; hideSearch: boolean; nbRecordPerPage: number };

// Definition of the U2104Context type
export interface U2104ContextType {
  luStateHandlers: ReturnType<typeof useU2000LUStateHandlers>;
  searchStateHandlers: U2000SearchStateHandlersReturn;
  gridStateHandlers: U2000GridStateHandlersReturn<U2104Organization>;
  gridOrgRoleStateHandlers: U2000GridStateHandlersReturn<U2104Role>;
  gridOrgAffiliationStateHandlers: U2000GridStateHandlersReturn<U2104Affiliation>;
  orgAffiliationCurrentTab: string | null;
  setOrgAffiliationCurrentTab: (tabId: string) => void;
}

//  Props for the U2104ContextProvider component
interface U2104ContextProviderProps {
  initLU: InitLU;
  children: ReactNode;
}

// Creation of the U2104Context
const U2104Context = React.createContext<U2104ContextType | undefined>(undefined);

// U2104ContextProvider component to provide context values
export const U2104ContextProvider: React.FC<U2104ContextProviderProps> = ({ initLU, children }) => {
  const U2000LUStateHandlers = useU2000LUStateHandlers({ initLU: initLU as InitLU & U2000LUState });
  const U2000SearchStateHandlers = useU2000SearchStateHandlers({ luName: "u2104", launchSearch: initLU.launchSearch, hideSearch: initLU.hideSearch });
  const U2000GridStateHandlers = useU2000GridStateHandlers<U2104Organization>({ nbRecordPerPage: initLU.nbRecordPerPage ?? 50 });
  const U2104GridOrgRoleStateHandlers = useU2000GridStateHandlers<U2104Role>();
  const U2104GridOrgAffiliitonStateHandlers = useU2000GridStateHandlers<U2104Affiliation>();

  const [orgAffiliationCurrentTab, setOrgAffiliationCurrentTab] = React.useState<string | null>(null);

  const contextValue = useMemo<U2104ContextType>(
    () => ({
      luStateHandlers: U2000LUStateHandlers,
      searchStateHandlers: U2000SearchStateHandlers,
      gridStateHandlers: U2000GridStateHandlers,
      gridOrgRoleStateHandlers: U2104GridOrgRoleStateHandlers,
      gridOrgAffiliationStateHandlers: U2104GridOrgAffiliitonStateHandlers,
      orgAffiliationCurrentTab,
      setOrgAffiliationCurrentTab,
    }),
    [U2000GridStateHandlers, U2000SearchStateHandlers, U2000LUStateHandlers, U2104GridOrgRoleStateHandlers, U2104GridOrgAffiliitonStateHandlers]
  );

  return <U2104Context.Provider value={contextValue}>{children}</U2104Context.Provider>;
};

// Custom hook to use the U2104Context
export function useU2104Context(): U2104ContextType {
  const context = useContext(U2104Context);
  if (!context) {
    throw new Error("useU2104Context must be used within a U2104ContextProvider");
  }
  return context;
}
